import React, { useState, useEffect, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import PeopleIcon from "@mui/icons-material/People";

import { VesselDetailsTab } from "../tabs/VesselDetailsTab";
import { VesselSummaryTab } from "../tabs/VesselSummaryTab";
import { VesselFunctionDefinitionTab } from "../tabs/VesselFunctionDefinitionTab";
import { CrewDefinitionTab } from "../tabs/CrewDefinitionTab";
import { ConfirmationModal } from "../../../common/Modal";
import { LoadingSpinner } from "../../../common/Loading";
import { Select } from "../../../common/Select";

// ── Empty form ────────────────────────────────────────────────────────────────
const emptyForm = {
  // Tab 1 — Vessel Details
  vesselId: "",
  vesselCode: "",
  registeredName: "",
  effectiveDate: "",
  vesselName: "",

  // Tab 2 — Vessel Summary
  vesselStatusId: "",
  areaOfOperationId: "",
  areaOfOperationCode: "",
  areaOfOperationDescription: "",
  subAreaId: "",
  subAreaCode: "",
  subAreaDescription: "",
  vesselTypeId: "",
  vesselClass: "",
  operationGroup: "",

  // Tab 3 — Vessel Function Definition
  vessel201Functions: [],

  // Tab 4 — Crew Definition
  crewDefinitions: [],
};

// ── Modal ─────────────────────────────────────────────────────────────────────
export const Vessel201Modal = ({
  isOpen,
  onClose,
  mode,           // "add" | "edit" | "view"
  selectedData,
  setSelectedData,
  selectedDataEffectiveDates,
  options,
  dropdownsLoading,
  onAdd,
  onUpdate,
  fetchDataByVesselIdEffectiveDate,
  fetchDropdowns,
}) => {
  const isViewOnly = mode === "view";
  const canSave = !isViewOnly;

  const [activeTab, setActiveTab] = useState("vessel-details");
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({}); // ADDED: errors state
  const [filterVesselEffectiveDate, setFilterVesselEffectiveDate] = useState("");

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: "",
    message: "",
    action: null,
  });



  // ── Field setter ──────────────────────────────────────────────────────────
  // MODIFIED: Clear error for field when value changes
  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleVesselEffectivityChange = async (e) => {
    const selectedValue = e.target.value;
    setFilterVesselEffectiveDate(selectedValue);

    if (selectedData && selectedData.vesselId) {
      const resultDetails = await fetchDataByVesselIdEffectiveDate(selectedData.vesselId, selectedValue);
      if (resultDetails) {
        setSelectedData(resultDetails);
      }
    }
  };

  // Calculate total allowed crews
  const allowedCrewListTotal = useMemo(() => {
    return (form.crewDefinitions || []).reduce((sum, cd) => {
      const allowed = parseInt(cd.allowedCrews) || 0;
      return sum + allowed;
    }, 0);
  }, [form.crewDefinitions]);

  // ADDED: Validate function ────────────────────────────────────────────────
  const validate = () => {
    const e = {};

    // Tab 1 — Vessel Details
    if (!form.vesselId) e.vesselId = "Vessel Code is required.";
    if (!form.effectiveDate) e.effectiveDate = "Effectivity Date is required.";

    // Tab 2 — Vessel Summary
    if (!form.vesselStatusId) e.vesselStatusId = "Vessel Status is required.";
    if (!form.areaOfOperationId) e.areaOfOperationId = "Area of Operation is required."; // ADDED
    if (!form.vesselTypeId) e.vesselTypeId = "Vessel Type is required.";

    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    // ADDED: Validate before showing confirmation
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);

      // ADDED: Auto-navigate to the tab with the first error
      if (validationErrors.vesselId || validationErrors.effectiveDate) {
        setActiveTab("vessel-details");
      } else if (validationErrors.vesselStatusId || validationErrors.areaOfOperationId || validationErrors.subAreaId || validationErrors.vesselTypeId) { // MODIFIED
        setActiveTab("vessel-summary");
      }

      return; // Stop submission if validation fails
    }

    // Clear errors if validation passes
    setErrors({});

    if (mode === "add") {
      setConfirmationConfig({
        title: "Create Vessel 201",
        message: "Save this vessel record?",
        action: doAdd,
      });
    } else {
      setConfirmationConfig({
        title: "Update Vessel 201",
        message: "Update this vessel record?",
        action: doUpdate,
      });
    }
    setShowConfirmation(true);
  };

  const doAdd = async () => {
    setConfirmLoading(true);
    await onAdd(form, () => {
      setShowConfirmation(false);
      onClose();
    });
    setConfirmLoading(false);
  };

  const doUpdate = async () => {
    setConfirmLoading(true);
    await onUpdate(form, selectedData.id, () => {
      setShowConfirmation(false);
      onClose();
    });
    setConfirmLoading(false);
  };


  // ── Sync form when modal opens ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setActiveTab("vessel-details");
      setErrors({}); // ADDED: reset errors when modal opens
      if (selectedData) {
        setForm({
          ...emptyForm,
          // Tab 1
          vesselId: selectedData.vesselId || "",
          vesselCode: selectedData.vesselCode || "",
          registeredName: selectedData.registeredName || "",
          effectiveDate: selectedData.effectiveDate?.split("T")[0] || "",
          vesselName: selectedData.vesselName || "",
          // Tab 2
          vesselStatusId: selectedData.vesselStatusId || "",
          areaOfOperationId: selectedData.areaOfOperationId || "",
          areaOfOperationCode: selectedData.areaOfOperationCode || "",
          areaOfOperationDescription: selectedData.areaOfOperationDescription || "",
          subAreaId: selectedData.subAreaId || "",
          subAreaCode: selectedData.subAreaCode || "",
          subAreaDescription: selectedData.subAreaDescription || "",
          vesselTypeId: selectedData.vesselTypeId || "",
          vesselClass: selectedData.vesselClass || "",
          operationGroup: selectedData.operationGroup || "",
          // Tab 3
          vessel201Functions: selectedData.vessel201Functions || [],
          // Tab 4
          crewDefinitions: (selectedData.crewDefinitions || []).map((cd) => ({
            id: cd.id || null,
            departmentId: cd.departmentId || "",
            departmentCode: cd.departmentCode || "",
            departmentDescription: cd.departmentDescription || "",
            positionId: cd.positionId || "",
            positionCode: cd.positionCode || "",
            positionDescription: cd.positionDescription || "",
            allowedCrews: cd.allowedCrews ?? "",
          })),
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [isOpen, selectedData]);

  // ── Sync dropdowns when modal opens ────────────────────────────────────────────
  useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);

  if (!isOpen) return null;

  // ── Tabs config ───────────────────────────────────────────────────────────
  const tabs = [
    { key: "vessel-details", label: "Vessel Details" },
    { key: "vessel-summary", label: "Vessel Summary" },
    { key: "vessel-function-definition", label: "Vessel Function Definition" },
    { key: "crew-definition", label: "Crew Definition" },
  ];

  // MODIFIED: Pass errors to tabs
  const sharedTabProps = { mode, form, set, options, isViewOnly, errors };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[95%] lg:w-[80%] xl:w-[75%] rounded-lg shadow-2xl flex flex-col h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            <DirectionsBoatIcon />
            {isViewOnly
              ? "View Vessel 201"
              : mode === "add"
                ? "Add Vessel 201"
                : "Edit Vessel 201"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {mode === "view" && selectedDataEffectiveDates && selectedDataEffectiveDates.length > 0 && (
          <div className="flex justify-end items-center px-6 py-3 border-b bg-white rounded-t-lg">
            <div className="w-full sm:w-1/2 md:w-1/3 lg:w-1/4">
              <Select
                label="Effectivity Dates:"
                options={selectedDataEffectiveDates}
                value={filterVesselEffectiveDate || ""}
                onChange={handleVesselEffectivityChange}
              />
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2
                  flex items-center gap-2 border-none cursor-pointer whitespace-nowrap
                  ${activeTab === tab.key
                    ? "border-b-2 border-blue-500 text-blue-600 bg-blue-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow bg-white">


          <div className="mx-auto space-y-6">
            {activeTab === "vessel-details" && <VesselDetailsTab {...sharedTabProps} />}
            {activeTab === "vessel-summary" && <VesselSummaryTab {...sharedTabProps} />}
            {activeTab === "vessel-function-definition" && <VesselFunctionDefinitionTab {...sharedTabProps} />}
            {activeTab === "crew-definition" && <CrewDefinitionTab {...sharedTabProps} />}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center gap-2 rounded-b-lg">
          {/* Left side - Total Allowed Crews */}
          <div className="flex items-center gap-2 text-sm">
            <PeopleIcon className="text-blue-600" style={{ fontSize: 20 }} />
            <span className="font-semibold text-gray-700">Total Allowed Crews:</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 font-bold rounded-md border border-blue-200">
              {allowedCrewListTotal}
            </span>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-400 text-white rounded cursor-pointer hover:bg-gray-500 border-none transition-colors"
            >
              Close
            </button>
            {canSave && (
              <button
                onClick={handleSubmit}
                className="px-8 py-2 bg-blue-600 text-white rounded font-bold cursor-pointer hover:bg-blue-700 border-none transition-colors"
              >
                {mode === "add" ? "Save Vessel" : "Update Vessel"}
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationConfig.action}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        isLoading={confirmLoading}
      />
      <LoadingSpinner loading={dropdownsLoading} />
    </div>
  );
};